import { useCallback, useEffect, useState } from 'react';
import { STORAGE_PREFIX } from './useWidgetFilters';

// Per-widget settings kept in the browser (same storage as the widget period filters)
const storageKey = (widgetId: string) => `${STORAGE_PREFIX}${widgetId}_settings`;

// The same settings can be open in several places at once (widget on the board + picker drawer),
// so every change is broadcast and each instance picks it up.
const SETTINGS_CHANGED_EVENT = 'dashboard-widget-settings-changed';
const ALL_WIDGETS = '*';

type SettingsChangedDetail = { widgetId: string; settings?: Record<string, unknown> };

const broadcast = (detail: SettingsChangedDetail) => {
	window.dispatchEvent(new CustomEvent<SettingsChangedDetail>(SETTINGS_CHANGED_EVENT, { detail }));
};

// Call after wiping the stored settings, so open instances fall back to their defaults
export const notifyWidgetSettingsCleared = () => broadcast({ widgetId: ALL_WIDGETS });

const loadFromStorage = <T extends Record<string, unknown>>(widgetId: string, defaults: T): T => {
	try {
		const raw = localStorage.getItem(storageKey(widgetId));
		if (!raw) return defaults;
		return { ...defaults, ...JSON.parse(raw) };
	} catch {
		return defaults;
	}
};

const useWidgetSettings = <T extends Record<string, unknown>>(widgetId: string, defaults: T): [T, (next: T) => void] => {
	const [settings, setSettingsState] = useState<T>(() => loadFromStorage(widgetId, defaults));

	useEffect(() => {
		const onChanged = (e: Event) => {
			const detail = (e as CustomEvent<SettingsChangedDetail>).detail;
			// Values travel in the event, so instances stay in sync even if the storage write failed
			if (detail.widgetId === widgetId && detail.settings) setSettingsState(detail.settings as T);
			else if (detail.widgetId === ALL_WIDGETS) setSettingsState(loadFromStorage(widgetId, defaults));
		};
		window.addEventListener(SETTINGS_CHANGED_EVENT, onChanged);
		return () => window.removeEventListener(SETTINGS_CHANGED_EVENT, onChanged);
	}, [widgetId, defaults]);

	const setSettings = useCallback((next: T) => {
		setSettingsState(next);
		try {
			localStorage.setItem(storageKey(widgetId), JSON.stringify(next));
		} catch {
			// Silent fail
		}
		broadcast({ widgetId, settings: next });
	}, [widgetId]);

	return [settings, setSettings];
};

export default useWidgetSettings;
